import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Center,
  Text,
  VStack,
  Badge,
  Icon,
  Tooltip,
  SimpleGrid,
} from "@chakra-ui/react";
import { ArrowUpIcon, ArrowDownIcon } from "@chakra-ui/icons";
import axios from "axios";
import proxy from "../../proxy";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await axios.get(`${proxy}server_mts/Transactions.php`, {
          headers: { "Cache-Control": "no-cache" },
        });

        if (!response.data || !Array.isArray(response.data)) {
          throw new Error("Invalid transactions data.");
        }

        setTransactions(response.data);
      } catch (err) {
        setError("Unable to retrieve the transactions.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff < 7) return `${diff} days ago`;
    if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;

    return formatDate(dateString);
  };

  const totalInflow = transactions.filter((t) => Number(t.out_in) === 0).length;
  const totalOutflow = transactions.filter((t) => Number(t.out_in) === 1).length;

  if (isLoading) {
    return (
      <Center minH="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="teal.300" thickness="4px" />
          <Text color="gray.400">Loading transactions...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="50vh">
        <VStack spacing={3}>
          <Text fontSize="5xl">⚠️</Text>
          <Text color="red.400" fontWeight="bold">
            {error}
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box maxW="1300px" mx="auto" p={6}>
      <VStack spacing={6} align="stretch">

        {/* TABLE SECTION */}
        <Box
          p={4}
          borderRadius="2xl"
          border="2px solid rgba(255,255,255,0.1)"
          bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
          boxShadow="0 10px 35px rgba(0,0,0,0.4)"
          overflowX="auto"
        >
          <Heading
            size="sm"
            textAlign="center"
            color="teal.200"
            mb={4}
            letterSpacing="1px"
          >
            📥 Latest 5 Transactions
          </Heading>

          <Table size="md">
            <Thead>
              <Tr bg="rgba(0,0,0,0.3)">
                {["Date", "Type", "Amount", "Asset", "Wallet"].map((h) => (
                  <Th
                    key={h}
                    color="teal.200"
                    fontSize="xs"
                    textAlign="center"
                    p={3}
                  >
                    {h}
                  </Th>
                ))}
              </Tr>
            </Thead>

            <Tbody>
              {transactions.map((tx, index) => {
                const isOut = Number(tx.out_in) === 0;

                return (
                  <Tr
                    key={index}
                    transition="0.2s"
                    _hover={{
                      bg: "rgba(255,255,255,0.05)",
                      transform: "scale(1.01)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                    }}
                  >
                    {/* DATE */}
                    <Td color="gray.300" textAlign="center" fontSize="sm" p={2}>
                      <Tooltip label={formatDate(tx.date_operation)} hasArrow>
                        <Text>{getRelativeTime(tx.date_operation)}</Text>
                      </Tooltip>
                    </Td>

                    {/* TYPE */}
                    <Td textAlign="center">
                      <Badge
                        px={3}
                        py={1}
                        borderRadius="full"
                        display="inline-flex"
                        alignItems="center"
                        gap={2}
                        bg={
                          isOut
                            ? "linear-gradient(135deg,#FC8181,#FEB2B2)"
                            : "linear-gradient(135deg,#48BB78,#68D391)"
                        }
                        color="white"
                        fontSize="xs"
                      >
                        <Icon as={isOut ? ArrowDownIcon : ArrowUpIcon} />
                        {isOut ? "Outflow" : "Inflow"}
                      </Badge>
                    </Td>

                    {/* AMOUNT */}
                    <Td
                      color={isOut ? "red.300" : "green.300"}
                      fontWeight="bold"
                      textAlign="center"
                      fontSize="md"
                    >
                      {isOut ? "-" : "+"}
                      {tx.amount}
                    </Td>

                    {/* ASSET */}
                    <Td textAlign="center">
                      <Badge
                        px={3}
                        py={1}
                        bg="linear-gradient(135deg,#9F7AEA,#B794F4)"
                        color="white"
                        borderRadius="full"
                        fontSize="xs"
                      >
                        {tx.symbol_crypto}
                      </Badge>
                    </Td>

                    {/* WALLET */}
                    <Td textAlign="center" color="cyan.300" fontSize="xs">
                      💾 Ledger
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </VStack>
    </Box>
  );
};

const StatBox = ({ label, value, color }) => (
  <Box
    p={4}
    bg="rgba(0,0,0,0.35)"
    borderRadius="xl"
    border={`2px solid ${color}33`}
    textAlign="center"
    transition="0.3s"
    _hover={{
      transform: "translateY(-4px) scale(1.02)",
      boxShadow: `0 10px 20px ${color}55`,
    }}
  >
    <Text fontSize="xs" color="gray.400" fontWeight="bold">
      {label}
    </Text>
    <Text
      mt={1}
      fontSize="2xl"
      fontWeight="black"
      color={color}
      textShadow={`0 0 12px ${color}55`}
    >
      {value}
    </Text>
  </Box>
);

export default Transactions;
